import { auth, db } from './firebase';
import { addDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { STATUS_BADGE_PACKAGE_CODE } from './constants/reportPackages';

// AUTOMATIC SWITCH:
// If running on localhost, use local backend.
// If deployed, use the Cloud Run URL.
const IS_LOCAL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const CLOUD_URL = process.env.REACT_APP_API_URL || 'https://analytics-server-652526389082.europe-west2.run.app';
const LOCAL_URL = 'http://localhost:8080';

const SERVER_URL = IS_LOCAL ? LOCAL_URL : CLOUD_URL;
export const getAnalyticsAssetVariant = () => {
  return 'canonical';
};

export const setAnalyticsAssetVariant = (variant) => {
  // App is canonical-only by policy; ignore caller input.
  return 'canonical';
};

const resolveAssetVariant = (_override) => 'canonical';
const withResolvedAssetVariant = (body, override) => ({
  ...body,
  assetVariant: resolveAssetVariant(override),
});

const normalizeWeeklyWeek = (week) => {
  if (!week || typeof week !== 'object') return week;
  const next = { ...week };
  if (next.ehiAssetPct == null) {
    if (next.ehiPct != null && Number.isFinite(Number(next.ehiPct))) {
      next.ehiAssetPct = Number(next.ehiPct);
    } else if (next.ehiAsset != null && Number.isFinite(Number(next.ehiAsset))) {
      next.ehiAssetPct = Number(next.ehiAsset) <= 1.0001 ? Number(next.ehiAsset) * 100 : Number(next.ehiAsset);
    }
  }
  if (next.ehiAsset == null && next.ehiAssetPct != null && Number.isFinite(Number(next.ehiAssetPct))) {
    next.ehiAsset = Number(next.ehiAssetPct) / 100;
  }
  return next;
};

const normalizeWeeklyPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  if (Array.isArray(payload.weeks)) {
    return { ...payload, weeks: payload.weeks.map(normalizeWeeklyWeek) };
  }
  if (Array.isArray(payload.parcels)) {
    return { ...payload, parcels: payload.parcels.map(normalizeWeeklyPayload) };
  }
  if (payload.years && typeof payload.years === 'object') {
    const years = {};
    for (const [year, yearData] of Object.entries(payload.years)) {
      years[year] = normalizeWeeklyPayload(yearData);
    }
    return { ...payload, years };
  }
  return payload;
};

const getAuthHeaders = async () => {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { 
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

/** Attach `status` (HTTP) and `code` (machine-readable error key) for
 *  user-facing error mapping. Prefers JSON `{ message }` (human-readable)
 *  when present, falling back to `{ error }` (legacy/code-only). */
async function throwIfNotOk(res, defaultMessage) {
  if (res.ok) return;
  let msg = defaultMessage;
  let code = null;
  try {
    const data = await res.json();
    if (data && typeof data.message === 'string' && data.message.trim()) {
      msg = data.message.trim();
      if (typeof data.error === 'string') code = data.error;
    } else if (data && typeof data.error === 'string') {
      msg = data.error;
      code = data.error;
    }
  } catch {
    msg = res.statusText || defaultMessage;
  }
  const err = new Error(msg);
  err.status = res.status;
  if (code) err.code = code;
  throw err;
}

/**
 * List EE assets for a farm.
 *
 * @param {string} farmId        Firestore property_id (used by the
 *                               analytics-server's verifyFarmAccess middleware
 *                               to gate the call against the caller's
 *                               `users.farmIds` membership).
 * @param {string} [assetFolder] Canonical EE asset-folder name. Optional and
 *                               equal to `farmId` for the legacy 5 + WLD
 *                               (`farmId === asset_folder` convention); set
 *                               to the mirrored `farms/{farmId}.assetFolder`
 *                               for claim_intake-slugged properties (e.g.
 *                               `OLMA` for `old-macdonalds-farm-9d9bc7`).
 *                               See PHASE_NEXT_to_marcus_integration_response_v20.md
 *                               and server v70.0 changelog.
 */
export const fetchFarmLayers = async (farmId, assetFolder = null) => {
  const headers = await getAuthHeaders();
  const body = { folder: farmId };
  if (assetFolder && assetFolder !== farmId) body.assetFolder = assetFolder;
  const res = await fetch(`${SERVER_URL}/list-ee-assets`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  await throwIfNotOk(res, 'Failed to fetch layers');
  const data = await res.json();
  return data.assets || [];
};

export const fetchVectorData = async (assetId, options = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-vector-data`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ assetId, ...options })
  });
  await throwIfNotOk(res, 'Failed to fetch vector data');
  return await res.json();
};

export const fetchMapTile = async (assetId, maskAssetId = null, options = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-map-token`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ assetId, maskAssetId, ...options })
  });
  await throwIfNotOk(res, 'Failed to get map token');
  return await res.json();
};

/**
 * Per-week SRC provenance for the EDX animation's percent-observed readout.
 * Resolves the anchored asset server-side; returns { available:false } for
 * legacy farms with no anchored asset. See /get-src-observed (Layer 1).
 */
export const fetchSrcObserved = async (assetId, maskAssetId = null) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-src-observed`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ assetId, maskAssetId })
  });
  await throwIfNotOk(res, 'Failed to get SRC provenance');
  return await res.json();
};

export const fetchTemporalMetadata = async (assetId, farmId = null, viewId = null) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-temporal-metadata`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ assetId, farmId, viewId })
  });
  await throwIfNotOk(res, 'Failed to get temporal metadata');
  return await res.json();
};

export const fetchTemporalSeries = async (assetId, metric = 'EHI', parcelId = null, year = null) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-temporal-series`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ assetId, metric, parcelId, year })
  });
  await throwIfNotOk(res, 'Failed to get temporal series');
  return await res.json();
};

// --- NEW ANALYTICS ENDPOINTS ---

export const fetchFarmSummary = async (farmId, years, { forceRefresh } = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-farm-summary`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, years, forceRefresh: !!forceRefresh })
  });
  await throwIfNotOk(res, 'Failed to fetch farm summary');
  return await res.json();
};

// Handoff 59: RB's action plan for sites whose farmSummary has it detached
// (`summary.detachedSurfaces` includes 'actions'). Returns { actions, _provenance }.
export const fetchFarmActions = async (farmId, { forceRefresh } = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-farm-actions`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, forceRefresh: !!forceRefresh })
  });
  await throwIfNotOk(res, 'Failed to fetch the published action plan');
  return await res.json();
};

// Handoff 60: RB's map lenses when the farmSummary has them detached
// (`summary.detachedSurfaces` includes 'map.lenses'). Returns { lenses, _provenance }.
export const fetchFarmMapLenses = async (farmId, { forceRefresh } = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-farm-map-lenses`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, forceRefresh: !!forceRefresh })
  });
  await throwIfNotOk(res, 'Failed to fetch the published map lenses');
  return await res.json();
};

export const fetchParcelStats = async (farmId, year) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-parcel-stats`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, year })
  });
  await throwIfNotOk(res, 'Failed to fetch parcel stats');
  return await res.json();
};

export const fetchClimateSummary = async (farmId, years) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-climate-summary`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, years })
  });
  await throwIfNotOk(res, 'Failed to fetch climate summary');
  return await res.json();
};

export const fetchTrajectory = async (farmId, years) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-trajectory`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, years })
  });
  if (!res.ok) throw new Error('Failed to fetch trajectory');
  return await res.json();
};

export const fetchCalibration = async (farmId, year) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-calibration`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, year })
  });
  await throwIfNotOk(res, 'Failed to fetch calibration');
  return await res.json();
};

export const fetchSatelliteFeed = async (farmId) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-satellite-feed`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId })
  });
  if (!res.ok) throw new Error('Failed to fetch satellite feed');
  return await res.json();
};

export const fetchDatasetAudits = async (farmId, forceRefresh = false) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-dataset-audits`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, forceRefresh })
  });
  await throwIfNotOk(res, 'Failed to fetch dataset audits');
  return await res.json();
};

export const fetchWeeklyScores = async (farmId, parcelId, year, includeEnsemble = true, { forceRefresh, assetVariant } = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-weekly-scores`, {
    method: 'POST', headers,
    body: JSON.stringify(withResolvedAssetVariant({ farmId, parcelId, year, includeEnsemble, forceRefresh: !!forceRefresh }, assetVariant))
  });
  await throwIfNotOk(res, 'Failed to fetch weekly scores');
  return normalizeWeeklyPayload(await res.json());
};

export const fetchFarmCurves = async (farmId, years, { forceRefresh, assetVariant } = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-farm-curves`, {
    method: 'POST', headers,
    body: JSON.stringify(withResolvedAssetVariant({ farmId, years, forceRefresh: !!forceRefresh }, assetVariant))
  });
  await throwIfNotOk(res, 'Failed to fetch farm curves');
  return normalizeWeeklyPayload(await res.json());
};

export const fetchFieldTopo = async (farmId, { forceRefresh } = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-field-topo`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, forceRefresh: !!forceRefresh })
  });
  if (!res.ok) throw new Error('Failed to fetch field topology');
  return await res.json();
};

export const fetchFieldTimeline = async (farmId, parcelId, startYear, endYear, { assetVariant } = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-field-timeline`, {
    method: 'POST', headers,
    body: JSON.stringify(withResolvedAssetVariant({ farmId, parcelId, startYear, endYear }, assetVariant))
  });
  await throwIfNotOk(res, 'Failed to fetch field timeline');
  return normalizeWeeklyPayload(await res.json());
};

export const fetchFieldElevation = async (farmId, parcelName, { forceRefresh, lowRes, overlays, edxYear, edxWeek } = {}) => {
  const headers = await getAuthHeaders();
  const body = { farmId, parcelName, forceRefresh: !!forceRefresh, lowRes: !!lowRes };
  if (overlays) body.overlays = overlays;
  if (edxYear) body.edxYear = edxYear;
  if (edxWeek) body.edxWeek = edxWeek;
  const res = await fetch(`${SERVER_URL}/get-field-elevation`, {
    method: 'POST', headers,
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    if (res.status === 404) {
      const e = new Error('ASSET_NOT_FOUND');
      e.status = 404;
      throw e;
    }
    if (res.status === 503) {
      const e = new Error('EE_NOT_READY');
      e.status = 503;
      throw e;
    }
    await throwIfNotOk(res, 'Failed to fetch field elevation');
  }
  return await res.json();
};

export const fetchFarmZone = async (farmId, forceRefresh = false) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-farm-zone`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, forceRefresh })
  });
  await throwIfNotOk(res, 'Failed to fetch farm zone');
  return await res.json();
};

export const fetchClimateEvents = async (farmId, year) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-climate-events`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, year })
  });
  await throwIfNotOk(res, 'Failed to fetch climate events');
  return await res.json();
};

export const fetchCarbonFlux = async (farmId, parcelId, year, { forceRefresh, assetVariant } = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-carbon-flux`, {
    method: 'POST', headers,
    body: JSON.stringify(withResolvedAssetVariant({ farmId, parcelId, year, forceRefresh: !!forceRefresh }, assetVariant))
  });
  await throwIfNotOk(res, 'Failed to fetch carbon flux');
  return await res.json();
};

export const fetchCarbonTrajectory = async (farmId, parcelId, years, { assetVariant } = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-carbon-trajectory`, {
    method: 'POST', headers,
    body: JSON.stringify(withResolvedAssetVariant({ farmId, parcelId, years }, assetVariant))
  });
  await throwIfNotOk(res, 'Failed to fetch carbon trajectory');
  return await res.json();
};

export const fetchCarbonIntelligence = async (farmId, { forceRefresh } = {}) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/get-carbon-intelligence`, {
    method: 'POST', headers,
    body: JSON.stringify({ farmId, forceRefresh: !!forceRefresh })
  });
  await throwIfNotOk(res, 'Failed to fetch carbon intelligence');
  return await res.json();
};

/** Admin-only. target: all | memory | firestore_all | responseCacheSummary | ... */
export const clearAdminAnalyticsCache = async (target) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  // Force-refresh so a long-lived admin tab does not hit "Invalid or expired token".
  const token = await user.getIdToken(true);
  const res = await fetch(`${SERVER_URL}/admin/clear-cache`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ target }),
  });
  await throwIfNotOk(res, 'Failed to clear cache');
  return await res.json();
};

export const fetchAdminSynthWeeklyParcelDebug = async (farmId, parcelId, year, { assetPathOverride } = {}) => {
  const headers = await getAuthHeaders();
  const body = { farmId, parcelId, year, assetVariant: 'canonical' };
  if (assetPathOverride) body.assetPathOverride = assetPathOverride;
  const res = await fetch(`${SERVER_URL}/admin/debug-synth-weekly-parcel`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  await throwIfNotOk(res, 'Failed to inspect synth weekly rows');
  return await res.json();
};

export const fetchAdminGenerateSiteDataWeekly = async (farmId, year, { assetPathOverride } = {}) => {
  const headers = await getAuthHeaders();
  const body = { farmId, year, assetVariant: 'canonical' };
  if (assetPathOverride) body.assetPathOverride = assetPathOverride;
  const res = await fetch(`${SERVER_URL}/admin/generate-site-data-weekly`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  await throwIfNotOk(res, 'Failed to generate site_data_weekly sidecar');
  return await res.json();
};

export const fetchAdminDiffSiteDataWeekly = async (farmId, year, parcelId, { referencePath, assetPathOverride } = {}) => {
  const headers = await getAuthHeaders();
  const body = { farmId, year, parcelId, assetVariant: 'canonical' };
  if (referencePath) body.referencePath = referencePath;
  if (assetPathOverride) body.assetPathOverride = assetPathOverride;
  const res = await fetch(`${SERVER_URL}/admin/diff-site-data-weekly`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  await throwIfNotOk(res, 'Failed to diff generated sidecar against reference JSON');
  return await res.json();
};

// --- v0.3 Phase 3: REPORT DOWNLOAD (subscription-package signed URL) ---

/**
 * Mint a short-lived (15 min) signed GCS URL for a published report file.
 *
 * Backend: GET /api/v1/properties/:propertyId/reports/:packageId/years/:year/download
 *
 * Returns `{ url, expires_at, file: {kind, size_bytes, sha256}, version,
 * is_current_version, snapshot_id, generated_at, candidate_label, ... }`.
 *
 * Auth/access errors are surfaced via `throwIfNotOk` (status + code on
 * the thrown Error). Common failure codes:
 *   - 401 unauthorized
 *   - 403 forbidden_no_farm_access            (caller can't read the property)
 *   - 403 forbidden_no_package_access         (caller has no grant for this package/year)
 *   - 403 forbidden_non_current_version       (non-admin asked for an older version)
 *   - 404 year_not_found / no_current_version (nothing published yet)
 *   - 404 version_not_found / no_files / file_kind_not_found
 *   - 503 signed_url_failed                   (Cloud Run IAM not set up — admin needs to act)
 *
 * @param {string} farmId      property_id
 * @param {string} packageId   one of: land_health | carbon | regen_practice | sustainability_esg
 * @param {number|string} year e.g. 2026
 * @param {object} [opts]
 * @param {string} [opts.kind] explicit file kind (defaults: 'pdf' or first)
 * @param {number} [opts.version] admin-only override for non-current versions
 */
export const getReportDownloadUrl = async (farmId, packageId, year, opts = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (opts.kind) params.set('kind', String(opts.kind));
  if (opts.version != null) params.set('version', String(opts.version));
  const qs = params.toString();
  const url = `${SERVER_URL}/api/v1/properties/${encodeURIComponent(farmId)}/reports/${encodeURIComponent(packageId)}/years/${encodeURIComponent(year)}/download${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { method: 'GET', headers });
  await throwIfNotOk(res, 'Failed to get report download URL');
  return await res.json();
};

/** Session cache: remounting Overview should not re-mint the six signed URLs. */
const overviewFigureCache = new Map();

/**
 * Resolve short-lived (15 min) signed URLs for the property Overview figures
 * (handoff 16). RB publishes the six Overview PNGs to the reports bucket and
 * carries their gcs_paths on `summary.overview.figures`; those are gcs_paths,
 * not URLs, and the summary is cached, so the URL must be minted at view time.
 *
 * Backend: POST /api/v1/properties/:propertyId/overview-figures/resolve
 *   body { farmId, figures: { <key>: gcsPath, ... } }
 *   -> { figures: { <key>: signedUrl, ... }, expires_at }
 *
 * Only keys with a valid, in-bucket, this-property gcs_path come back; unknown
 * or missing figures are simply absent (the card then shows its placeholder).
 *
 * @param {string} farmId  property_id
 * @param {object} figures { kite, parcel_map, edx, hillshade, twi, carbon } gcs_paths
 * @returns {Promise<object>} map of figure key -> signed URL
 */
export const resolveOverviewFigures = async (farmId, figures) => {
  if (!farmId || !figures || typeof figures !== 'object') return {};
  const pathsKey = JSON.stringify(figures);
  const cached = overviewFigureCache.get(farmId);
  if (cached && cached.pathsKey === pathsKey && cached.expiresAt > Date.now() + 30_000) {
    return cached.figures;
  }
  const headers = await getAuthHeaders();
  const url = `${SERVER_URL}/api/v1/properties/${encodeURIComponent(farmId)}/overview-figures/resolve`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ farmId, figures }),
  });
  await throwIfNotOk(res, 'Failed to resolve overview figures');
  const data = await res.json();
  const resolved = (data && data.figures) || {};
  const expiresAt = data?.expires_at ? Date.parse(data.expires_at) : (Date.now() + 14 * 60 * 1000);
  overviewFigureCache.set(farmId, {
    pathsKey,
    figures: resolved,
    expiresAt: Number.isFinite(expiresAt) ? expiresAt : (Date.now() + 14 * 60 * 1000),
  });
  return resolved;
};

/** Session cache: Diagnostics mints ~40 signed URLs; remounting the tab should not wait again. */
const diagnosticsFigureCache = new Map();

/**
 * Resolve short-lived signed URLs for Diagnostics figures (handoff 22).
 * Same access + signing pattern as overview-figures/resolve, but for paths under
 * `{propertyId}/diagnostics-figures/` and an open key set (RB emits many names).
 *
 * @param {string} farmId
 * @param {object} figures map of figure key -> gcs_path
 * @returns {Promise<object>} map of figure key -> signed URL
 */
export const resolveDiagnosticsFigures = async (farmId, figures) => {
  if (!farmId || !figures || typeof figures !== 'object') return {};
  const pathsKey = JSON.stringify(figures);
  const cached = diagnosticsFigureCache.get(farmId);
  if (cached && cached.pathsKey === pathsKey && cached.expiresAt > Date.now() + 30_000) {
    return cached.figures;
  }
  const headers = await getAuthHeaders();
  const url = `${SERVER_URL}/api/v1/properties/${encodeURIComponent(farmId)}/diagnostics-figures/resolve`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ farmId, figures }),
  });
  await throwIfNotOk(res, 'Failed to resolve diagnostics figures');
  const data = await res.json();
  const resolved = (data && data.figures) || {};
  const expiresAt = data?.expires_at ? Date.parse(data.expires_at) : (Date.now() + 14 * 60 * 1000);
  diagnosticsFigureCache.set(farmId, {
    pathsKey,
    figures: resolved,
    expiresAt: Number.isFinite(expiresAt) ? expiresAt : (Date.now() + 14 * 60 * 1000),
  });
  return resolved;
};

/**
 * Resolve a short-lived (15 min) signed URL for the property's shareable
 * "Share your status" badge image (the 1200x630 social card).
 *
 * The badge is produced + pushed by ReportBuilder through the SAME publish
 * pipeline as reports (AppDevPromptsMngmnt/handoffs/00-foundation.md §D), so we
 * reuse the report-download signed-URL endpoint with the dedicated badge
 * package code (STATUS_BADGE_PACKAGE_CODE). No `kind` is forced: the endpoint's
 * file-resolution falls back to the single attached image when no PDF/HTML kind
 * matches, so the badge image URL comes back regardless of the exact `kind`
 * label RB attached.
 *
 * Access mirrors reports exactly (admin / public-demo / paid-or-granted owner),
 * since it goes through the same endpoint. Throws `throwIfNotOk` errors with
 * `status`/`code`; callers should treat 404 (year/version/file_not_found) as
 * "no badge published yet" and degrade gracefully.
 *
 * YEAR DISCOVERY (2026-06-23 fix): the badge — like every RB deliverable — is
 * filed under the PUBLICATION year, which is NOT the same as the latest
 * analytics data year. WLD's badge is published under 2026 while the EE-derived
 * analytics summary's latest complete year is 2025, so a caller that guessed
 * the year from `summary.currentYear.year` (or `now-1`) asked for the wrong
 * year and got a spurious 404. We therefore DISCOVER the latest published badge
 * year from `farms/{id}/reports/{badge}/years/*` (the same discovery model
 * FarmReportsPage uses for report editions) and use that. The caller-supplied
 * `year` is kept only as a fallback for when discovery can't run.
 *
 * @param {string} farmId  property_id
 * @param {number|string} [year]  fallback data year if discovery fails
 * @param {object} [opts]  passthrough to getReportDownloadUrl (e.g. { kind })
 * @returns {Promise<object>} the download response ({ url, expires_at, ... })
 */
export const getStatusBadgeUrl = async (farmId, year, opts = {}) => {
  let targetYear = year ?? null;
  try {
    const discovered = await getLatestPublishedBadgeYear(farmId);
    if (discovered != null) targetYear = discovered;
  } catch (e) {
    // Discovery is best-effort (rules / empty / offline) — fall back to the
    // caller-supplied year so the badge still resolves where the guess happens
    // to be right.
    console.warn('[getStatusBadgeUrl] badge-year discovery failed; using fallback year', year, e?.message || e);
  }
  if (targetYear == null) {
    const err = new Error('No published badge year could be resolved for this property.');
    err.status = 404;
    throw err;
  }
  return getReportDownloadUrl(farmId, STATUS_BADGE_PACKAGE_CODE, targetYear, opts);
};

/**
 * Find the most recent year that has a published status-badge edition for a
 * property by probing `farms/{farmId}/reports/{STATUS_BADGE_PACKAGE_CODE}/years`
 * and returning the highest year whose doc carries a `current_version`. Mirrors
 * FarmReportsPage's per-code discovery so the badge surfaces under whatever year
 * RB published it, decoupled from the analytics data year. Returns `null` when
 * no badge edition exists (callers treat that as "no badge yet").
 *
 * @param {string} farmId  property_id
 * @returns {Promise<number|null>} latest published badge year, or null
 */
export const getLatestPublishedBadgeYear = async (farmId) => {
  if (!farmId) return null;
  const snap = await getDocs(
    collection(db, 'farms', farmId, 'reports', STATUS_BADGE_PACKAGE_CODE, 'years')
  );
  let latest = null;
  snap.forEach((d) => {
    const hasCurrent = typeof d.data()?.current_version === 'number';
    const yr = Number(d.id);
    if (hasCurrent && Number.isFinite(yr) && (latest == null || yr > latest)) {
      latest = yr;
    }
  });
  return latest;
};

// --- v0.3 Phase 4: ADMIN LIFECYCLE + REPORT UNLOCK ---

/**
 * Admin: transition a property's buildStatus.
 *
 * Backend: POST /api/v1/admin/properties/:propertyId/lifecycle
 *
 * @param {string} propertyId
 * @param {'retry'|'reactivate'|'cancel'} action
 * @param {string} [reason] optional ≤200-char audit-log note
 *
 * Common error codes (on `err.code`):
 *   - 400 invalid_action
 *   - 403 admin_required
 *   - 404 property_not_found
 *   - 409 invalid_transition (response includes `current_status`)
 *   - 500 lifecycle_write_failed
 */
export const setPropertyLifecycle = async (propertyId, action, reason) => {
  const headers = await getAuthHeaders();
  const res = await fetch(
    `${SERVER_URL}/api/v1/admin/properties/${encodeURIComponent(propertyId)}/lifecycle`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ action, reason: reason || undefined }),
    }
  );
  await throwIfNotOk(res, 'Lifecycle action failed');
  return await res.json();
};

/**
 * Admin: set or clear the unlock_until window on a (property, package, year)
 * report edition. Per spec §6, `unlock_until` overrides `locked === true`
 * while active so RB can push a corrected version after the auto-unlock
 * budget (2 per year) is exhausted.
 *
 * Backend: POST /api/v1/admin/properties/:propertyId/reports/:packageId/years/:year/unlock
 *
 * @param {string} propertyId
 * @param {string} packageId one of the canonical 4 (land_health|carbon|regen_practice|sustainability_esg)
 * @param {number|string} year
 * @param {string|null} untilIso ISO 8601 string for the unlock expiry, or null to clear
 * @param {string} [reason] optional ≤200-char audit-log note
 *
 * Common error codes (on `err.code`):
 *   - 400 invalid_until / invalid_year / unknown_package_id
 *   - 403 admin_required
 *   - 404 year_not_found
 *   - 500 unlock_write_failed
 */
export const setReportYearUnlock = async (propertyId, packageId, year, untilIso, reason) => {
  const headers = await getAuthHeaders();
  const body = untilIso === null
    ? { clear: true, reason: reason || undefined }
    : { until: untilIso, reason: reason || undefined };
  const res = await fetch(
    `${SERVER_URL}/api/v1/admin/properties/${encodeURIComponent(propertyId)}/reports/${encodeURIComponent(packageId)}/years/${encodeURIComponent(year)}/unlock`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
  );
  await throwIfNotOk(res, 'Unlock action failed');
  return await res.json();
};

// --- SUBSCRIPTION ENDPOINTS ---

/**
 * Start a Stripe Checkout session for a Founding Associate subscription.
 *
 * @param {'monthly'|'annual'} [plan='monthly']
 *   Selects which Stripe Price the backend bills against:
 *     - 'monthly' → STRIPE_PRICE_ID         (£55/mo)
 *     - 'annual'  → STRIPE_PRICE_ID_ANNUAL  (£600/yr)
 *   Defaults to 'monthly' so older callers keep working.
 */
export const createCheckoutSession = async (plan = 'monthly') => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/create-checkout-session`, {
    method: 'POST', headers,
    body: JSON.stringify({
      plan,
      // Backend also ensures ?session_id={CHECKOUT_SESSION_ID} for Stripe substitution
      successUrl: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: window.location.origin + '/cancel',
    })
  });
  // Use the shared throwIfNotOk so `err.status` and `err.code` are attached
  // — formatUserFacingError() then maps 503 / 401 / etc. to a clearer hint
  // instead of the generic "Something went wrong".
  await throwIfNotOk(res, 'Failed to create checkout session');
  return await res.json();
};

/**
 * Submit a "Contact us" enquiry from the corporate ESG plan card.
 *
 * Writes a doc to Firestore (`corporateEnquiries/{auto-id}`) with the user's
 * uid + a few self-attested fields. A Cloud Function picks the doc up and
 * emails the configured admin recipients (`systemConfig/adminNotifications.recipientEmails`),
 * which is where Marcus's address is registered. We deliberately do NOT trust
 * the client to send any sensitive profile data — the function reads
 * `users/{uid}` server-side via the Admin SDK to assemble the enquiry email.
 *
 * @param {object} [opts]
 * @param {string} [opts.message] Optional free-text note from the user.
 * @param {string} [opts.country] Optional ISO country code to tag the enquiry
 *   (used by the geo-block "register interest" path for out-of-region buyers).
 * @param {string} [opts.source]  Optional origin tag (defaults to 'checkout_page').
 * @returns {Promise<{ id: string }>}
 */
export const submitCorporateEnquiry = async ({ message, country, source } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to send a corporate enquiry.');
  const ref = await addDoc(collection(db, 'corporateEnquiries'), {
    uid: user.uid,
    email: user.email || '',
    displayName: user.displayName || '',
    message: typeof message === 'string' ? message.slice(0, 2000) : '',
    country: typeof country === 'string' ? country.slice(0, 8) : '',
    source: typeof source === 'string' && source ? source : 'checkout_page',
    createdAt: serverTimestamp(),
  });
  return { id: ref.id };
};

/** After Stripe redirects to /success?session_id=cs_…, sync Firestore if webhook was slow (requires auth). */
export const verifyCheckoutSession = async (sessionId) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/stripe/verify-checkout-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ sessionId }),
  });
  await throwIfNotOk(res, 'Could not verify checkout session');
  return await res.json();
};

export const cancelSubscription = async () => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/cancel-subscription`, {
    method: 'POST', headers,
    body: JSON.stringify({})
  });
  await throwIfNotOk(res, 'Failed to cancel subscription');
  return await res.json();
};

/**
 * Self-service account deletion from the profile page. Deletes the caller's
 * Firestore user doc, sweeps their property intake draft, and removes their
 * Firebase Auth identity. When the subscription is already `cancelling` or
 * `cancelled`, Stripe is left on its existing cancel-at-period-end schedule.
 *
 * Server-side guards:
 *   - `admin_self_delete_blocked`              — caller has role === 'admin'
 *   - `active_subscription_must_cancel_first`  — active/past_due Stripe sub
 *   - `user_not_found`                         — no Firestore doc and no Auth identity
 *
 * @returns {Promise<{ status: 'user_deleted', target_uid: string, target_email: string|null, stripe_cancelled: boolean|null, auth_deleted: boolean|null, ... }>}
 */
export const deleteAccount = async () => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/delete-account`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });
  await throwIfNotOk(res, 'Failed to delete account');
  return await res.json();
};

/**
 * Admin-initiated subscription cancellation. Schedules cancel-at-period-end
 * on another user's Stripe subscription. Gated server-side by `verifyAdmin`
 * (Firestore `users/{caller}.role === 'admin'`) AND business rules:
 *   - target user must exist
 *   - target must NOT have `subscriptionOverride === true` (admin should
 *     toggle the override off instead — there's no Stripe sub to cancel)
 *   - target must NOT be `role === 'admin'` (no Stripe sub on admin accts)
 *   - target must have a `stripeSubscriptionId` on file
 *
 * The server returns 400 with one of these error codes when a guard
 * trips: `uid_required` | `override_account` | `admin_user` |
 * `no_subscription` | `user_not_found`. The FE in
 * `frontend/src/pages/admin/UsersAdmin.js` maps these to inline
 * actionable messages in the edit modal.
 *
 * Firestore is updated by the Stripe `customer.subscription.updated`
 * webhook, not by this call — keeps both self-service and admin
 * cancellation paths sharing one source of truth.
 *
 * @param {string} uid  The Firestore document id of the target user.
 * @returns {Promise<{ status: string, target_uid: string, target_email: string|null, stripe_subscription_id: string }>}
 */
export const adminCancelUserSubscription = async (uid) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/admin/cancel-user-subscription`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ uid }),
  });
  await throwIfNotOk(res, 'Failed to cancel subscription');
  return await res.json();
};

/**
 * Admin-initiated full user deletion. Cancels the target user's Stripe
 * subscription IMMEDIATELY (regardless of whether it's 'active' or
 * 'cancelling') and then deletes their Firestore user doc.
 *
 * The Stripe cancellation runs FIRST, so a Stripe failure leaves the
 * user doc intact for retry rather than orphaning billing on a missing
 * row. A `resource_missing` on the Stripe side (sub already gone) is
 * treated as success and the user doc is still deleted.
 *
 * Server-side guards (return 400 with structured error codes):
 *   - `uid_required`        — empty body
 *   - `user_not_found`      — no Firestore doc for this uid
 *   - `self_delete_blocked` — caller is the same uid as target (avoid
 *                              locking yourself out of the admin console)
 *
 * @param {string} uid  The Firestore document id of the user to delete.
 * @returns {Promise<{ status: 'user_deleted', target_uid: string, target_email: string|null, stripe_subscription_id: string|null, stripe_cancelled: boolean|null, stripe_cancel_error: string|null }>}
 *   `stripe_cancelled` is `null` when there was no sub on file, `true`
 *   when the cancellation succeeded, `false` when it failed (still
 *   safe — the user doc was deleted anyway; check
 *   `stripe_cancel_error` if you want to surface the Stripe error to
 *   the admin).
 */
export const adminDeleteUser = async (uid) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/admin/delete-user`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ uid }),
  });
  await throwIfNotOk(res, 'Failed to delete user');
  return await res.json();
};

/**
 * Secondary, Auth-only destructive action. Removes the Firebase Authentication
 * identity for `uid` if it still exists. Used by the Admin console as a
 * follow-up when `adminDeleteUser` reported `auth_deleted: false` (the main
 * delete cleaned up Firestore/Stripe but couldn't remove the Auth user), or to
 * clean up an orphaned Auth identity whose Firestore doc is already gone.
 *
 * Does NOT touch Firestore or Stripe — those are handled by adminDeleteUser.
 * Idempotent: returns `{ status: 'auth_user_absent', existed: false }` when
 * there was nothing to delete.
 *
 * @param {string} uid  The Firebase Auth uid to remove.
 * @returns {Promise<{ status: string, target_uid: string, target_email?: string|null, existed: boolean, auth_deleted: boolean|null, auth_delete_error?: string }>}
 */
export const adminDeleteAuthUser = async (uid) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/admin/delete-auth-user`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ uid }),
  });
  await throwIfNotOk(res, 'Failed to delete Firebase authentication');
  return await res.json();
};

/**
 * Read-only listing of every Firebase Auth identity. Used by the Users admin
 * page to show a per-row "has a sign-in identity?" badge and to surface
 * orphaned Auth users (an Auth record with no matching users/{uid} doc).
 *
 * @returns {Promise<{ count: number, users: Array<{ uid: string, email: string|null, disabled: boolean, creationTime: string|null, lastSignInTime: string|null, providers: string[] }> }>}
 */
export const adminListAuthUsers = async () => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/admin/auth-users`, { headers });
  await throwIfNotOk(res, 'Failed to list Firebase authentication users');
  return await res.json();
};

// --- v0.3 PROPERTY INTAKE ---

/**
 * Submit a new property intake (Phase 2 of the v0.3 contract rollout).
 *
 * Posts the user-collected `user_facing.*` fields to a NEW app-facing endpoint
 * that the analytics-server will own:
 *
 *   POST /api/v1/properties/intake
 *
 * The server is responsible for:
 *   - Allocating a `property_id` (uuid),
 *   - Verifying the caller's plan-tier property cap,
 *   - Writing `propertyConfigs/{property_id}` with `buildStatus: 'intake_submitted'`,
 *   - Mirroring the doc to `farms/{property_id}` (so legacy UI keeps rendering),
 *   - Adding the new id to `users/{uid}.farmIds`,
 *   - Returning `{ property_id, build_status }`.
 *
 * Until that endpoint ships the helper will reject with HTTP 404 — the
 * Phase 2 frontend handles that gracefully and surfaces an "intake server not
 * yet available" message via the form's error path.
 *
 * @param {object} payload
 * @param {object} payload.user_facing  See §8.2 of the v0.3 contract — keys
 *   include property_name, address, managers[], tenure, sbi, description,
 *   objectives, assessment_purpose, enterprise_types[], farm_size_ha_estimate,
 *   certifications[], operational_emissions_tco2, consent, training, challenges.
 * @param {object} [payload.intake_meta]  Optional client metadata (e.g.
 *   approximate_location, boundary_upload_token).
 * @returns {Promise<{ property_id: string, build_status: string }>}
 */
export const submitPropertyIntake = async (payload) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/api/v1/properties/intake`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {}),
  });
  await throwIfNotOk(res, 'Failed to submit property intake');
  return await res.json();
};

/**
 * Upload disclosure Lift evidence for a property/framework (handoff 10 §5a).
 *
 * POSTs base64 file payloads to the per-property, private evidence store
 * (`POST /api/v1/properties/:id/disclosure-evidence?fw=<frameworkId>`) and
 * returns the stored `[{ name, path }]` refs. SAME Firebase ID-token auth +
 * property-owner check as the report pipeline; the store itself is a dedicated,
 * private, EU-region GCS bucket reached only server-side.
 *
 * The backend endpoint + GCS store are a flagged follow-up (bucket provisioning
 * is EcoIntel-owned, tracked separately). Until it is deployed this call will
 * fail and the Lift flow keeps a filename-only fallback, so the representation
 * is still recorded. Callers should treat any throw as "keep filenames".
 *
 * @param {string} propertyId  property_id
 * @param {string} frameworkId framework key, e.g. 'sbtn'
 * @param {Array<{name:string,type?:string,b64:string}>} files
 * @returns {Promise<Array<{name:string,path:string}>>} the stored refs
 */
export const uploadDisclosureEvidence = async (propertyId, frameworkId, files) => {
  const headers = await getAuthHeaders();
  const qs = frameworkId ? `?fw=${encodeURIComponent(frameworkId)}` : '';
  const url = `${SERVER_URL}/api/v1/properties/${encodeURIComponent(propertyId)}/disclosure-evidence${qs}`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ files: files || [] }),
  });
  await throwIfNotOk(res, 'Failed to upload disclosure evidence');
  const j = await res.json();
  return Array.isArray(j?.stored) ? j.stored : [];
};

/**
 * Fetch the client-held disclosure inputs for a property (handoff 10 §3a).
 *
 * GET /api/v1/properties/:id/disclosure -> { ...inputs, etag } | {} when none.
 * SAME Firebase ID-token auth + property-owner check as the report pipeline.
 * These are the operator's own facts (entity, period, governance, targets,
 * policies, operational emissions) plus the Lift `attestations` map; EcoIntel
 * never publishes or shares them.
 *
 * @param {string} propertyId  property_id
 * @returns {Promise<object>} the inputs doc (with `etag`), or {} if unset.
 */
export const fetchDisclosureInputs = async (propertyId) => {
  const headers = await getAuthHeaders();
  const url = `${SERVER_URL}/api/v1/properties/${encodeURIComponent(propertyId)}/disclosure`;
  const res = await fetch(url, { method: 'GET', headers });
  await throwIfNotOk(res, 'Failed to load disclosure inputs');
  const j = await res.json();
  return j && typeof j === 'object' ? j : {};
};

/**
 * Save (full replace) the client-held disclosure inputs (handoff 10 §3a).
 *
 * PUT /api/v1/properties/:id/disclosure with `{ ...inputs, etag }`. The server
 * is etag-guarded: on a stale etag it returns 409 (re-GET, merge, retry).
 * Returns `{ etag }` (the new server etag) on success.
 *
 * @param {string} propertyId  property_id
 * @param {object} body        the full inputs doc, including the last-seen etag
 * @returns {Promise<{ etag: string }>}
 */
export const saveDisclosureInputs = async (propertyId, body) => {
  const headers = await getAuthHeaders();
  const url = `${SERVER_URL}/api/v1/properties/${encodeURIComponent(propertyId)}/disclosure`;
  const res = await fetch(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body || {}),
  });
  if (res.status === 409) {
    const j = await res.json().catch(() => ({}));
    const err = new Error('stale_etag');
    err.code = 'stale_etag';
    err.etag = j?.etag || null;
    throw err;
  }
  await throwIfNotOk(res, 'Failed to save disclosure inputs');
  const j = await res.json();
  return { etag: j?.etag || null };
};

/**
 * Resolve one Lift-evidence object to a short-TTL signed read URL (handoff 10 §5a).
 *
 * GET /api/v1/properties/:id/disclosure-evidence/resolve?path=<opaque key>.
 * Owner gets the URL inline; non-owner support access is consent-gated + audited
 * server-side. Returns the signed URL string.
 *
 * @param {string} propertyId  property_id
 * @param {string} path        the opaque object key stored on the attestation
 * @returns {Promise<string>} a 10-minute signed read URL
 */
export const resolveDisclosureEvidence = async (propertyId, path) => {
  const headers = await getAuthHeaders();
  const url = `${SERVER_URL}/api/v1/properties/${encodeURIComponent(propertyId)}/disclosure-evidence/resolve?path=${encodeURIComponent(path)}`;
  const res = await fetch(url, { method: 'GET', headers });
  await throwIfNotOk(res, 'Failed to resolve disclosure evidence');
  const j = await res.json();
  return j?.url || null;
};

/**
 * Delete one Lift-evidence object (handoff 10 §5a). Owner only.
 *
 * DELETE /api/v1/properties/:id/disclosure-evidence?path=<opaque key>.
 *
 * @param {string} propertyId  property_id
 * @param {string} path        the opaque object key to remove
 * @returns {Promise<void>}
 */
export const deleteDisclosureEvidence = async (propertyId, path) => {
  const headers = await getAuthHeaders();
  const url = `${SERVER_URL}/api/v1/properties/${encodeURIComponent(propertyId)}/disclosure-evidence?path=${encodeURIComponent(path)}`;
  const res = await fetch(url, { method: 'DELETE', headers });
  await throwIfNotOk(res, 'Failed to delete disclosure evidence');
};

/**
 * Emit an app-side property event to the analytics-server's audit ingress.
 *
 * Fire-and-forget. Called from ParcelEditor.handleSave AFTER the direct
 * Firestore parcel write has succeeded — so the parcel doc is already
 * in its post-save state, this call's only job is to land the audit
 * row + the `farms/{id}.sync_meta` stamp Marcus's GET /config reads to
 * populate `sync_state.last_app_write_at` / `etag_at_last_app_write`
 * / `pending_actions`. See SYNC_STATE_implementation_design_v01.md and
 * PHASE_NEXT_to_marcus_integration_response_v23.md for the wire shape.
 *
 * Failure modes are non-fatal: any error is logged to console and
 * swallowed. The user-facing save has already committed, and the
 * audit row's only consumer is Marcus's RB publisher (which will
 * silently miss this one event but pick up the resulting parcel-doc
 * change on its next config diff regardless — sync_state is the
 * acceleration mechanism, not the correctness mechanism).
 *
 * Idempotency key is client-generated so a retry (network blip + the
 * user immediately re-presses Save Field) doesn't double-emit. Format:
 *   `<uid>:<property_id>:<parcel_id>:<ms>:<rand>`
 * — opaque to the server, just needs to be unique-ish across realistic
 * retry windows.
 *
 * @param {object} params
 * @param {string} params.propertyId        Firestore farm id, e.g. 'OLMA' or 'old-macdonalds-farm-9d9bc7'.
 * @param {string} [params.parcelId]        Parcel doc id (camelCase shape on the wire is `parcel_id`).
 * @param {string[]} params.fieldsChanged   List of changed camelCase field names; server maps to event_type.
 * @param {object} [params.before]          Pre-save snapshot of changed fields (for `previous_value`).
 * @param {object} [params.after]           Post-save snapshot of changed fields (for `new_value`).
 * @returns {Promise<void>}                 Resolves once the call completes (success OR caught failure).
 */
export const emitPropertyEvent = async ({
  propertyId,
  parcelId = null,
  fieldsChanged = [],
  before = null,
  after = null,
} = {}) => {
  try {
    if (!propertyId) return;
    if (!Array.isArray(fieldsChanged) || fieldsChanged.length === 0) return;

    const user = auth.currentUser;
    if (!user) return;  // signed-out users can't write parcels; if we got here something else is wrong
    const uid = user.uid;

    // Idempotency key: uid + property + parcel + ms + 6-char random. The
    // random suffix means two saves within the same millisecond (rare
    // but possible if React's event loop microtasks two clicks before
    // the first commit returns) generate distinct keys; the ms keeps
    // them roughly chronological in the idempotency collection.
    const ms = Date.now();
    const rand = Math.random().toString(36).slice(2, 8);
    const idemKey = `${uid}:${propertyId}:${parcelId || 'farm'}:${ms}:${rand}`;

    const headers = await getAuthHeaders();
    const body = {
      property_id: propertyId,
      parcel_id: parcelId || null,
      fields_changed: fieldsChanged,
      before: before || null,
      after: after || null,
      idempotency_key: idemKey,
    };
    const res = await fetch(`${SERVER_URL}/api/v1/app/property-event`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      // Log status + body so production-debugging from a user report
      // can correlate to a specific failure mode (403 = access drift,
      // 409 = idempotency replay collision, 500 = server bug).
      let detail = '';
      try { detail = (await res.text()).slice(0, 400); } catch (_) { /* ignore */ }
      console.warn(`[emitPropertyEvent] non-OK: ${res.status} ${detail}`);
      return;
    }
    // No need to consume the body — the save already succeeded UI-side.
    // We just needed the network call to land.
  } catch (err) {
    console.warn('[emitPropertyEvent] failed:', err?.message || err);
  }
};

/**
 * Fire the farmer-ready signal for Marcus's scheduler-driven RB pipeline
 * (marcus_v30 §1). Sister to emitPropertyEvent but with a discrete
 * one-shot intent shape: this is "I've checked my parcels, build my
 * reports", not a parcel field-diff.
 *
 * Unlike emitPropertyEvent, this call IS awaited and surfaces success /
 * failure to the caller via the resolved object. The CTA wants to flip
 * its UI to a confirmation chip on success and show a retry affordance
 * on failure — fire-and-forget would leave the button in an ambiguous
 * "did that actually do anything?" state.
 *
 * Server responsibilities (backend/analytics-server/appEvents.js):
 *   - Verifies Firebase ID token + property-access (mirrors firestore.rules canAccessFarm).
 *   - Rejects with 400 acceptance_required unless acceptance_confirmed === true.
 *   - Writes ONE propertyOpsEvents row of kind app.farmer_ordered_reports
 *     with the snapshot payload Marcus's daemon polls for.
 *   - Atomically merges `farmer_ordered_at: serverTimestamp,
 *     farmer_acceptance_confirmed: true` onto farms/{id}.sync_meta.
 *   - Idempotency replay (same key + same body) returns the original
 *     event_id and replayed:true; same key + different body returns 409.
 *
 * @param {object} params
 * @param {string} params.propertyId             Firestore farm id (e.g. 'OLMA' or 'old-macdonalds-farm-9d9bc7').
 * @param {object} params.parcelStateSnapshot    Counts computed by the caller from the parcels sub-collection.
 *   Shape: { total_parcels, renamed_count, with_notes_count,
 *            with_metadata_count, with_field_conditions_count }. Missing
 *   fields are coerced to 0 server-side; passing the full object is
 *   preferred so Marcus's pipeline can audit what the farmer saw at
 *   click-time.
 * @returns {Promise<{ ok: boolean, status?: number, error?: string, event_id?: string|null, ordered_at?: string|null, replayed?: boolean }>}
 *   Resolves (never rejects) with a structured outcome so the CTA can
 *   render a deterministic next state.
 */
export const emitBuildMyReports = async ({ propertyId, parcelStateSnapshot } = {}) => {
  try {
    if (!propertyId) return { ok: false, error: 'property_id_required' };

    const user = auth.currentUser;
    if (!user) return { ok: false, error: 'unauthenticated' };
    const uid = user.uid;

    // Same idempotency-key shape as emitPropertyEvent but with a `ready`
    // discriminator in the parcel slot so a parcel-edit replay and a
    // ready-for-reports replay can never collide in
    // appEventIdempotencyKeys/.
    const ms = Date.now();
    const rand = Math.random().toString(36).slice(2, 8);
    const idemKey = `${uid}:${propertyId}:ready:${ms}:${rand}`;

    const headers = await getAuthHeaders();
    const body = {
      property_id: propertyId,
      acceptance_confirmed: true,
      parcel_state_snapshot: parcelStateSnapshot || {},
      idempotency_key: idemKey,
    };
    const res = await fetch(`${SERVER_URL}/api/v1/app/property-event/ready-for-reports`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let detail = '';
      let code = null;
      try {
        const errBody = await res.json();
        if (errBody && typeof errBody === 'object') {
          code = typeof errBody.error === 'string' ? errBody.error : null;
          detail = typeof errBody.message === 'string' ? errBody.message : '';
        }
      } catch (_) { /* non-JSON body — leave detail blank */ }
      console.warn(`[emitBuildMyReports] non-OK: ${res.status} ${code || ''} ${detail}`);
      return { ok: false, status: res.status, error: code || `http_${res.status}` };
    }
    let payload = null;
    try { payload = await res.json(); } catch (_) { /* tolerate empty body */ }
    return {
      ok: true,
      event_id: payload?.event_id || null,
      ordered_at: payload?.ordered_at || null,
      replayed: !!payload?.replayed,
    };
  } catch (err) {
    console.warn('[emitBuildMyReports] failed:', err?.message || err);
    return { ok: false, error: err?.message || 'network_error' };
  }
};

/**
 * Handoff 30 Item 5 — download property/field CSV (Excel-openable).
 * Farmer-auth route: /api/v1/app/properties/:id/exchange/download
 */
export const downloadSiteExchange = async (propertyId, kind) => {
  const headers = await getAuthHeaders();
  delete headers['Content-Type'];
  const url = `${SERVER_URL}/api/v1/app/properties/${encodeURIComponent(propertyId)}/exchange/download?kind=${encodeURIComponent(kind)}`;
  const res = await fetch(url, { method: 'GET', headers });
  await throwIfNotOk(res, 'Failed to download spreadsheet');
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const match = /filename="?([^"]+)"?/i.exec(cd);
  const filename = match?.[1] || `${propertyId}_${kind === 'fields' ? 'field' : 'property'}_data.csv`;
  const a = document.createElement('a');
  const href = URL.createObjectURL(blob);
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
};

/**
 * Handoff 30 Item 5 — dry-run or apply an edited workbook.
 * @param {string} propertyId
 * @param {File} file
 * @param {{ dryRun?: boolean, applyToken?: string|null }} opts
 */
export const ingestSiteExchange = async (propertyId, file, { dryRun = true, applyToken = null } = {}) => {
  const headers = await getAuthHeaders();
  const toBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const i = result.indexOf(',');
      resolve(i >= 0 ? result.slice(i + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(f);
  });
  const file_base64 = file ? await toBase64(file) : null;
  const res = await fetch(
    `${SERVER_URL}/api/v1/app/properties/${encodeURIComponent(propertyId)}/exchange/ingest`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        dry_run: !!dryRun,
        file_name: file?.name || 'upload.csv',
        file_base64,
        apply_token: applyToken || undefined,
      }),
    },
  );
  await throwIfNotOk(res, 'Failed to ingest spreadsheet');
  return res.json();
};

/**
 * Handoff 32 — Third-party services consent (Omnia first).
 * Farmer-auth routes under /api/v1/app/properties/:id/connected-services.
 * The app captures consent; EcoIntel's server job does the Omnia pull.
 */
const connectedServicesUrl = (propertyId, suffix = '') =>
  `${SERVER_URL}/api/v1/app/properties/${encodeURIComponent(propertyId)}/connected-services${suffix}`;

export const getConnectedServices = async (propertyId) => {
  const headers = await getAuthHeaders();
  const res = await fetch(connectedServicesUrl(propertyId), { method: 'GET', headers });
  await throwIfNotOk(res, 'Failed to load third-party services');
  return res.json();
};

export const activateThirdPartyServices = async (propertyId) => {
  const headers = await getAuthHeaders();
  const res = await fetch(connectedServicesUrl(propertyId, '/activate'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ acknowledge: true }),
  });
  await throwIfNotOk(res, 'Failed to switch on Third-party services');
  return res.json();
};

export const acknowledgeConnectedServicesAgreement = async (propertyId) => {
  const headers = await getAuthHeaders();
  const res = await fetch(connectedServicesUrl(propertyId, '/acknowledge'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ acknowledge: true }),
  });
  await throwIfNotOk(res, 'Failed to accept the updated agreement');
  return res.json();
};

export const deactivateThirdPartyServices = async (propertyId) => {
  const headers = await getAuthHeaders();
  const res = await fetch(connectedServicesUrl(propertyId, '/deactivate'), {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });
  await throwIfNotOk(res, 'Failed to switch off Third-party services');
  return res.json();
};

export const activateConnectedService = async (propertyId, provider, body) => {
  const headers = await getAuthHeaders();
  const res = await fetch(
    connectedServicesUrl(propertyId, `/services/${encodeURIComponent(provider)}/activate`),
    { method: 'POST', headers, body: JSON.stringify(body || {}) },
  );
  await throwIfNotOk(res, 'Failed to connect this service');
  return res.json();
};

export const withdrawConnectedService = async (propertyId, provider) => {
  const headers = await getAuthHeaders();
  const res = await fetch(
    connectedServicesUrl(propertyId, `/services/${encodeURIComponent(provider)}/withdraw`),
    { method: 'POST', headers, body: JSON.stringify({}) },
  );
  await throwIfNotOk(res, 'Failed to switch this service off');
  return res.json();
};

export const downloadConnectedServicesRecord = async (propertyId) => {
  const headers = await getAuthHeaders();
  delete headers['Content-Type'];
  const res = await fetch(connectedServicesUrl(propertyId, '/export'), { method: 'GET', headers });
  await throwIfNotOk(res, 'Failed to download your connected-services record');
  const blob = await res.blob();
  const a = document.createElement('a');
  const href = URL.createObjectURL(blob);
  a.href = href;
  a.download = `${propertyId}_connected-services.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
};

const credentialsUrl = (propertyId, suffix = '') =>
  `${SERVER_URL}/api/v1/app/properties/${encodeURIComponent(propertyId)}/credentials${suffix}`;

export const getCredentialPublication = async (propertyId) => {
  const headers = await getAuthHeaders();
  const res = await fetch(credentialsUrl(propertyId), { method: 'GET', headers });
  await throwIfNotOk(res, 'Failed to load credential settings');
  return res.json();
};

export const saveCredentialToggles = async (propertyId, body) => {
  const headers = await getAuthHeaders();
  const res = await fetch(credentialsUrl(propertyId, '/toggles'), {
    method: 'POST',
    headers,
    body: JSON.stringify(body || {}),
  });
  await throwIfNotOk(res, 'Failed to save credential publication');
  return res.json();
};

export const saveCredentialFlavour = async (propertyId, body) => {
  const headers = await getAuthHeaders();
  const res = await fetch(credentialsUrl(propertyId, '/flavour'), {
    method: 'POST',
    headers,
    body: JSON.stringify(body || {}),
  });
  await throwIfNotOk(res, 'Failed to save credential page content');
  return res.json();
};

export const uploadCredentialPhoto = async (propertyId, { fileName, contentType, dataUrl }) => {
  const headers = await getAuthHeaders();
  const res = await fetch(credentialsUrl(propertyId, '/photo'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      file_name: fileName,
      content_type: contentType,
      file_base64: dataUrl,
    }),
  });
  await throwIfNotOk(res, 'Failed to upload the photo');
  return res.json();
};

export const getCredentialVettingQueue = async () => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${SERVER_URL}/api/v1/app/admin/credentials/queue`, {
    method: 'GET',
    headers,
  });
  await throwIfNotOk(res, 'Failed to load the credential review queue');
  return res.json();
};

export const vetCredentialFlavour = async (propertyId, { decision, note }) => {
  const headers = await getAuthHeaders();
  const res = await fetch(
    `${SERVER_URL}/api/v1/app/admin/credentials/${encodeURIComponent(propertyId)}/vet`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ decision, note }),
    },
  );
  await throwIfNotOk(res, 'Failed to record the review decision');
  return res.json();
};
